import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { usePageTitle } from '@/hooks/usePageTitle';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { WorkingDaysEditor, formatWorkingDays } from '@/components/attendance/WorkingDaysEditor';
import {
  Calendar,
  Mail,
  ArrowLeft,
  Loader2,
  Clock,
  CheckCircle2,
  XCircle,
  AlertCircle,
  TrendingUp,
  TrendingDown,
  Briefcase
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

export const LeaveProfilePage = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { userProfile, company, can } = useAuth();
  const canManageAttendance = can('attendance.manage');
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);

  usePageTitle(user ? `${user.first_name || ''} ${user.last_name || ''}`.trim() || 'Leave Profile' : 'Leave Profile');
  const [balance, setBalance] = useState(null);
  const [leaveHistory, setLeaveHistory] = useState([]);
  const [upcomingLeave, setUpcomingLeave] = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [workingDays, setWorkingDays] = useState(null);
  const [companyWorkingDays, setCompanyWorkingDays] = useState([1, 2, 3, 4, 5]);

  useEffect(() => {
    if (userProfile && company) {
      loadUserData();
    }
  }, [userProfile, company, userId]);

  const loadUserData = async () => {
    try {
      setLoading(true);
      const currentYear = new Date().getFullYear();
      const today = new Date().toISOString().split('T')[0];

      // SECURITY: First verify the user being viewed is in the same company
      const { data: membershipCheck, error: membershipError } = await supabase
        .from('company_members')
        .select('id')
        .eq('user_id', userId)
        .eq('company_id', company.id)
        .single();

      if (membershipError || !membershipCheck) {
        // User is not in the same company - this is a security violation attempt
        console.error('Security: Attempted to view leave profile from different company');
        toast.error('You do not have permission to view this leave profile');
        navigate('/attendance');
        return;
      }

      // Load user profile
      const { data: userData, error: userError } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (userError) throw userError;

      // Load current year balance
      const { data: balanceData } = await supabase
        .from('leave_balances')
        .select('*')
        .eq('user_id', userId)
        .eq('year', currentYear)
        .single();

      // Load all leave requests for this user
      const { data: requestsData } = await supabase
        .from('leave_requests')
        .select('*')
        .eq('user_id', userId)
        .order('start_date', { ascending: false });

      // Load leave types to check for admin-only types
      const { data: leaveTypesData } = await supabase
        .from('leave_request_types')
        .select('*')
        .or(`company_id.eq.${company.id},company_id.is.null`)
        .eq('is_active', true);

      // Load company_members to get working_days override
      const { data: memberData } = await supabase
        .from('company_members')
        .select('working_days')
        .eq('user_id', userId)
        .eq('company_id', company.id)
        .single();

      // Load company_leave_settings to get company default working days
      const { data: leaveSettingsData } = await supabase
        .from('company_leave_settings')
        .select('working_days')
        .eq('company_id', company.id)
        .single();

      // Separate into history and upcoming
      const upcoming = requestsData?.filter(req => req.start_date >= today && req.status === 'approved') || [];
      const history = requestsData || [];

      setUser(userData);
      setBalance(balanceData);
      setLeaveHistory(history);
      setUpcomingLeave(upcoming);
      if (leaveTypesData) {
        setLeaveTypes(leaveTypesData);
      }
      // Set working days (user override or null)
      setWorkingDays(memberData?.working_days || null);
      // Set company default working days
      setCompanyWorkingDays(leaveSettingsData?.working_days || [1, 2, 3, 4, 5]);
    } catch (error) {
      console.error('Failed to load user data:', error);
      toast.error('Failed to load leave profile');
    } finally {
      setLoading(false);
    }
  };

  // Check if a leave type should be hidden from the current user
  const shouldHideLeaveType = (leaveType, requestOwnerId) => {
    // If user is viewing their own profile, always show the full type
    if (requestOwnerId === userProfile?.id) {
      return false;
    }

    // If user is admin/manager, always show the full type
    if (canManageAttendance) {
      return false;
    }

    // Find the leave type configuration
    const leaveTypeConfig = leaveTypes.find(lt =>
      lt.name.toLowerCase().replace(/\s+/g, '_') === leaveType ||
      lt.id === leaveType
    );

    // If leave type is admin-only, hide it from non-admins
    if (leaveTypeConfig?.required_permission === 'attendance.manage') {
      return true;
    }

    return false;
  };

  const getLeaveTypeName = (type, requestOwnerId) => {
    // If should hide, return generic "Leave"
    if (shouldHideLeaveType(type, requestOwnerId)) {
      return 'Leave';
    }

    const names = {
      'annual_leave': 'Annual Leave',
      'sick_leave': 'Sick Leave',
      'unpaid_leave': 'Unpaid Leave',
      'work_from_home': 'Work From Home',
      'compassionate_leave': 'Compassionate Leave',
      'parental_leave': 'Parental Leave',
      'training': 'Training'
    };
    return names[type] || 'Leave';
  };

  const getLeaveTypeColor = (type, requestOwnerId) => {
    // If should hide, return generic gray color
    if (shouldHideLeaveType(type, requestOwnerId)) {
      return 'bg-gray-100 text-gray-800 border-gray-200';
    }

    const colors = {
      'annual_leave': 'bg-blue-100 text-blue-800 border-blue-200',
      'sick_leave': 'bg-red-100 text-red-800 border-red-200',
      'unpaid_leave': 'bg-gray-100 text-gray-800 border-gray-200',
      'work_from_home': 'bg-purple-100 text-purple-800 border-purple-200',
      'compassionate_leave': 'bg-orange-100 text-orange-800 border-orange-200',
      'parental_leave': 'bg-pink-100 text-pink-800 border-pink-200',
      'training': 'bg-green-100 text-green-800 border-green-200'
    };
    return colors[type] || colors['annual_leave'];
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved':
        return <CheckCircle2 className="h-4 w-4 text-green-600" />;
      case 'rejected':
        return <XCircle className="h-4 w-4 text-red-600" />;
      case 'pending':
        return <AlertCircle className="h-4 w-4 text-yellow-600" />;
      case 'cancelled':
        return <XCircle className="h-4 w-4 text-gray-600" />;
      default:
        return null;
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      'approved': 'bg-green-100 text-green-800 border-green-200',
      'rejected': 'bg-red-100 text-red-800 border-red-200',
      'pending': 'bg-yellow-100 text-yellow-800 border-yellow-200',
      'cancelled': 'bg-gray-100 text-gray-800 border-gray-200'
    };

    return (
      <Badge className={cn("flex items-center gap-1", styles[status] || styles.pending)}>
        {getStatusIcon(status)}
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const calculateUsagePercentage = () => {
    if (!balance || balance.entitled_days === 0) return 0;
    return Math.round((balance.taken_days / balance.entitled_days) * 100);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-violet-600" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex flex-col items-center justify-center h-96 gap-4">
        <p className="text-gray-600">User not found</p>
        <Link to="/attendance/team" className="text-violet-600 hover:text-violet-800">
          ← Back to Team Overview
        </Link>
      </div>
    );
  }

  const usagePercent = calculateUsagePercentage();

  return (
    <div className="space-y-6">
      {/* Back Button & Header */}
      <div className="flex items-center gap-4">
        <Link
          to="/attendance/team"
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Team Overview
        </Link>
      </div>

      {/* User Info Card */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-start gap-6">
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center flex-shrink-0">
              <span className="text-white font-semibold text-2xl">
                {user.first_name[0]}{user.last_name[0]}
              </span>
            </div>
            <div className="flex-1">
              <h1 className="text-3xl font-bold text-gray-900">
                {user.first_name} {user.last_name}
              </h1>
              <div className="flex items-center gap-2 mt-2 text-gray-600">
                <Mail className="h-4 w-4" />
                {user.email}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Leave Balance Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-gray-600 mb-1">Entitled Days</p>
              <p className="text-3xl font-bold text-gray-900">
                {balance?.entitled_days || 0}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-gray-600 mb-1">Days Taken</p>
              <p className="text-3xl font-bold text-orange-600">
                {balance?.taken_days || 0}
              </p>
              <div className="flex items-center justify-center gap-1 mt-1 text-xs text-gray-500">
                {usagePercent}% used
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-gray-600 mb-1">Remaining</p>
              <p className="text-3xl font-bold text-green-600">
                {balance?.remaining_days || 0}
              </p>
              <div className="flex items-center justify-center gap-1 mt-1 text-xs">
                {usagePercent < 50 ? (
                  <>
                    <TrendingDown className="h-3 w-3 text-green-600" />
                    <span className="text-green-600">Low usage</span>
                  </>
                ) : usagePercent >= 80 ? (
                  <>
                    <TrendingUp className="h-3 w-3 text-red-600" />
                    <span className="text-red-600">High usage</span>
                  </>
                ) : (
                  <span className="text-gray-500">Normal usage</span>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-gray-600 mb-1">Upcoming Leave</p>
              <p className="text-3xl font-bold text-violet-600">
                {upcomingLeave.length}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {upcomingLeave.reduce((sum, req) => sum + (req.calculated_days || 0), 0)} days
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Working Pattern */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Briefcase className="h-5 w-5 text-violet-600" />
            Working Pattern
          </CardTitle>
          <CardDescription>
            {workingDays ? 'Custom working days pattern' : 'Using company default pattern'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <WorkingDaysEditor
              value={workingDays || companyWorkingDays}
              companyDefault={companyWorkingDays}
              useCompanyDefault={!workingDays}
              disabled={true}
              showDefaultToggle={false}
              size="sm"
            />
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">
                Working days: {formatWorkingDays(workingDays || companyWorkingDays)}
              </span>
              {workingDays && (
                <Badge variant="outline" className="text-xs">
                  Custom
                </Badge>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Upcoming Leave */}
      {upcomingLeave.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Upcoming Leave
            </CardTitle>
            <CardDescription>Approved leave requests starting from today</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {upcomingLeave.map((request) => (
                <div
                  key={request.id}
                  className="flex items-center gap-4 p-4 border rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <Badge className={getLeaveTypeColor(request.leave_type, userId)}>
                    {getLeaveTypeName(request.leave_type, userId)}
                  </Badge>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 text-sm text-gray-900 font-medium">
                      <Clock className="h-4 w-4 text-gray-400" />
                      {formatDate(request.start_date)} - {formatDate(request.end_date)}
                      <span className="text-gray-500">({request.calculated_days} days)</span>
                    </div>
                    {request.user_note && (userId === userProfile?.id || canManageAttendance) && (
                      <p className="text-sm text-gray-600 mt-1">{request.user_note}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Leave History */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Leave History
          </CardTitle>
          <CardDescription>All leave requests for this user</CardDescription>
        </CardHeader>
        <CardContent>
          {leaveHistory.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Calendar className="h-12 w-12 mx-auto mb-2 text-gray-300" />
              <p>No leave history found</p>
            </div>
          ) : (
            <div className="space-y-3">
              {leaveHistory.map((request) => (
                <div
                  key={request.id}
                  className="flex items-start gap-4 p-4 border rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <Badge className={getLeaveTypeColor(request.leave_type, userId)}>
                        {getLeaveTypeName(request.leave_type, userId)}
                      </Badge>
                      {getStatusBadge(request.status)}
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-900">
                      <Clock className="h-4 w-4 text-gray-400" />
                      {formatDate(request.start_date)} - {formatDate(request.end_date)}
                      <span className="text-gray-500">({request.calculated_days} days)</span>
                    </div>
                    {request.user_note && (userId === userProfile?.id || canManageAttendance) && (
                      <p className="text-sm text-gray-600 mt-2 italic">
                        Note: {request.user_note}
                      </p>
                    )}
                    {request.admin_note && (
                      <p className="text-sm text-orange-600 mt-2">
                        Admin note: {request.admin_note}
                      </p>
                    )}
                    <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                      <span>Requested: {formatDate(request.created_at)}</span>
                      {request.reviewed_at && (
                        <span>Reviewed: {formatDate(request.reviewed_at)}</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
